import {
	utcTimestampToLocalDate,
	dateToUTCString,
	startOf,
	dateToQuery,
	parseDateFromQuery,
	endOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	Badge,
	Box,
	Button,
	Icons,
	Table,
	Stack,
	RelativeTime,
	TabContent,
	Tabs,
	TabsList,
	Text,
	Select,
	Money,
	useBreakpoints,
	Inline,
	Time,
	Grid,
	Col,
	Heading,
	CheckboxInput,
	Spinner,
	Divider,
	AsyncSelect,
} from "@sembark-travel/ui-base"
import {
	useLocationQuery,
	queryToSearch,
	Link,
} from "@sembark-travel/ui-router"
import {
	useSearch,
	areAdvancedFiltersAppliedDefault,
	TSearchParams,
	Search,
	ListView,
	SortOptions,
	TSortOptions,
} from "@sembark-travel/ui-list"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import React, {
	Dispatch,
	Fragment,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react"
import useSWR from "swr"
import { AddCommentInDialog, CommentableCommentItem } from "../Comments"
import {
	ApiRequestStatus,
	IInstallmentsCursorResponse,
	IInstalment,
} from "./store"
import {
	InfiniteScroll,
	InstalmentItemInDialog,
	VerifyPaymentInDialog,
} from "./InstalmentItem"
import { InstalmentContact } from "./InstalmentContact"
import { LogTransaction } from "./LogTransaction"
import {
	CheckboxInputField,
	DatePickerField,
	DateRangePickerField,
	EmptyNumberValidator,
	Form,
	GetFieldValue,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { generatePath, useLocation } from "../router-utils"
import config from "../config"
import {
	PERMISSIONS,
	useAuthUser,
	useCheckPermissions,
	useHasFeatureFlag,
} from "../Auth"
import { TemporaryStorage } from "../TemporaryStorage"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { Dialog, DialogBody, DialogFooter } from "@sembark-travel/ui-dialog"
import { SelectPendingPaymentCurrencies } from "../Currencies"
import * as Validator from "yup"
import { instalmentsXHR } from "./utils"
import { SelectHotels, SelectSelfBookHotel } from "../Hotels"
import { SelectTransportAndActivitySuppliers } from "../Supplier"
import { SelectTripSources } from "../TripSources"
import { IAccount, SelectAccount } from "../Accounting"
import {
	convertMoneyToCurrency,
	currencyPairToHuman,
	formatMoneyByDecimal,
	makeCurrencyPair,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { SelectTripOwners, SelectTrips } from "../Trips/List"
import { SelectTripDestination } from "../TripDestinations"
import { IUser } from "../Users"

const supplierTypeOptions = [
	{ id: "suppliers", name: "Suppliers" },
	{ id: "hotel_bookings", name: "Direct Hotel" },
]

const refundOptions = [
	{ id: "0", name: "Non Refund Only" },
	{ id: "1", name: "Only Refund" },
]

type TPaymentContext =
	| "incoming"
	| "outgoing"
	| "hotel"
	| "suppliers"
	| "hotelGroup"
	| "tripSource"

interface IPaymentsListProps {
	/**
	 * Show payments that are will get credited to the company account
	 */
	credit?: boolean
	/**
	 * Show payments that are will get debited to the company account
	 */
	debit?: boolean
	/**
	 * Title for the listing page
	 */
	title?: string
	/**
	 * Show the payments for given hotels only
	 */
	hotels?: Array<number> | Array<string>
	/**
	 * Show the payments for given transport service providers only
	 */
	transportServiceProviders?: Array<number> | Array<string>
	/**
	 * Hide contact details
	 */
	hideContact?: boolean
	/**
	 *   payments from which entity
	 */
	paymentContext: TPaymentContext

	/**	 * Show the payments for given trip source ids only
	 * */
	tripSourceIds?: Array<number> | Array<string>
}

const PAYMENT_CONTEXT_PERMISSIONS: Record<
	TPaymentContext,
	{
		filter_by_paymentable_types: boolean
		filter_by_hotels: boolean
		filter_by_trip_source_ids: boolean
		filter_by_transport_service_providers: boolean
		filter_by_non_refund_only: boolean
		defaultPayment: "incoming" | "outgoing"
		can_log_multiple: boolean
		is_refund: boolean
		filter_by_trip_destination_ids: boolean
		sales_person_filter: boolean
	}
> = {
	incoming: {
		filter_by_paymentable_types: true,
		filter_by_hotels: true,
		filter_by_trip_source_ids: true,
		filter_by_transport_service_providers: true,
		filter_by_non_refund_only: false,
		defaultPayment: "outgoing",
		can_log_multiple: false,
		is_refund: true,
		filter_by_trip_destination_ids: true,
		sales_person_filter: true,
	},
	outgoing: {
		filter_by_paymentable_types: true,
		filter_by_hotels: true,
		filter_by_trip_source_ids: true,
		filter_by_transport_service_providers: true,
		filter_by_non_refund_only: false,
		defaultPayment: "outgoing",
		can_log_multiple: true,
		is_refund: true,
		filter_by_trip_destination_ids: true,
		sales_person_filter: false,
	},
	suppliers: {
		filter_by_paymentable_types: true,
		filter_by_hotels: false,
		filter_by_trip_source_ids: false,
		filter_by_transport_service_providers: false,
		filter_by_non_refund_only: true,
		defaultPayment: "outgoing",
		can_log_multiple: true,
		is_refund: false,
		filter_by_trip_destination_ids: true,
		sales_person_filter: false,
	},
	hotel: {
		filter_by_paymentable_types: false,
		filter_by_hotels: false,
		filter_by_trip_source_ids: false,
		filter_by_transport_service_providers: false,
		filter_by_non_refund_only: true,
		defaultPayment: "outgoing",
		can_log_multiple: true,
		is_refund: false,
		filter_by_trip_destination_ids: false,
		sales_person_filter: false,
	},
	hotelGroup: {
		filter_by_paymentable_types: false,
		filter_by_hotels: false,
		filter_by_trip_source_ids: false,
		filter_by_transport_service_providers: false,
		filter_by_non_refund_only: false,
		defaultPayment: "outgoing",
		can_log_multiple: false,
		is_refund: false,
		filter_by_trip_destination_ids: true,
		sales_person_filter: false,
	},
	tripSource: {
		filter_by_paymentable_types: false,
		filter_by_hotels: false,
		filter_by_trip_source_ids: false,
		filter_by_transport_service_providers: false,
		filter_by_non_refund_only: true,
		can_log_multiple: false,
		is_refund: false,
		filter_by_trip_destination_ids: false,
		defaultPayment: "incoming",
		sales_person_filter: false,
	},
}

export function PaymentsList({
	credit,
	debit,
	hotels,
	transportServiceProviders,
	hideContact,
	paymentContext,
	tripSourceIds,
}: IPaymentsListProps) {
	const [refreshKey, setRefreshKey] = useState(0)
	const location = useLocation()

	const [query, setQuery] = useLocationQuery<IFilters, IFiltersInLocationQuery>(
		{ toQuery: paramsToLocationQuery, fromQuery: locationQueryToParams }
	)
	const [params, setParams] = useSearch(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const extraParams = useMemo(
		() => ({
			is_credit: credit ? 1 : undefined,
			is_debit: debit ? 1 : undefined,
			hotels: hotels && hotels.length ? hotels : undefined,
			transportServiceProviders:
				transportServiceProviders && transportServiceProviders.length
					? transportServiceProviders
					: undefined,
			trip_source_ids:
				tripSourceIds && tripSourceIds.length ? tripSourceIds : undefined,
		}),
		[credit, debit, hotels, transportServiceProviders, tripSourceIds]
	)
	const togglePaymentTypePath = useMemo(() => {
		const nextPaymentType = debit ? "incoming" : "outgoing"
		return location.pathname.replace(/(incoming|outgoing)$/, nextPaymentType)
	}, [debit, location.pathname])

	const getFilters = useCallback(
		() => (
			<Filters
				key={paymentContext + transportServiceProviders}
				supplyer_ids={transportServiceProviders as number[] | undefined}
				paymentContext={paymentContext}
			/>
		),
		[paymentContext, transportServiceProviders]
	)

	const { hasPermission } = useCheckPermissions()
	const canManageAccounting = hasPermission(PERMISSIONS.MANAGE_ACCOUNTING)

	const defaultParamsOfMultipleLog =
		useMemo((): TMultiLogPaymentsFilterFormType => {
			if (paymentContext === "hotelGroup" || paymentContext === "incoming")
				return {}
			const data = filtersToAPIParams(params)
			return {
				hotels: hotels?.length
					? { id: String(hotels[0]), name: String(hotels[0]) }
					: params.hotels && params.hotels.length > 0
						? params.hotels[0]
						: undefined,
				transportServiceProviders: transportServiceProviders?.length
					? {
							id: String(transportServiceProviders[0]),
							name: String(transportServiceProviders[0]),
						}
					: params.transport_service_providers &&
						  params.transport_service_providers.length > 0
						? params.transport_service_providers[0]
						: undefined,

				trip_destination_ids: params.trip_destination_ids || undefined,
				paymentable_types: params.hotels
					? undefined
					: params.paymentable_types && params.paymentable_types.length > 0
						? params.paymentable_types[0]
						: undefined,
				due_before: data.due_before_local
					? new Date(data.due_before_local)
					: undefined,
				due_after: data.due_after_local
					? new Date(data.due_after_local)
					: undefined,
				is_paid: data.is_paid,
				is_overdue: data.is_overdue,
				verified: data.verified,
			}
		}, [paymentContext, params, hotels, transportServiceProviders])

	return (
		<>
			<Search
				key={refreshKey}
				title={credit ? "Incoming Payments" : "Outgoing Payments"}
				initialParams={params}
				Filters={getFilters}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
				resetParams={(params) => ({
					q: "",
					status: params.status,
					page: 1,
				})}
				areAdvancedFiltersApplied={(params) => {
					const {
						status,
						is_refund,
						hide_on_hold,
						back,
						verified,
						...otherParams
					} = params
					return Boolean(
						is_refund ||
						hide_on_hold ||
						(verified && status === "paid") ||
						areAdvancedFiltersAppliedDefault(otherParams)
					)
				}}
				actions={() => (
					<Inline gap="2">
						{debit &&
						canManageAccounting &&
						PAYMENT_CONTEXT_PERMISSIONS[paymentContext].can_log_multiple ? (
							<LogMultiplePayments
								defaultParamsOfMultipleLog={defaultParamsOfMultipleLog}
								paymentContext={paymentContext}
								onSuccess={() => setRefreshKey((v) => v + 1)}
							>
								{({ onLogMultiple }) => (
									<Button onClick={() => onLogMultiple()}>
										Log Multiple <Icons.BankNotes />
									</Button>
								)}
							</LogMultiplePayments>
						) : null}
						{PAYMENT_CONTEXT_PERMISSIONS[paymentContext]
							.filter_by_non_refund_only ? (
							<Link
								anchored
								color="accent"
								fontWeight="semibold"
								to={togglePaymentTypePath}
							>
								<Button level="primary">
									{(debit &&
										PAYMENT_CONTEXT_PERMISSIONS[paymentContext]
											.defaultPayment === "outgoing") ||
									(credit &&
										PAYMENT_CONTEXT_PERMISSIONS[paymentContext]
											.defaultPayment === "incoming") ? (
										<>
											Refund Only{" "}
											<Icons.ArrowDownLeft fontWeight={"semibold"} />
										</>
									) : (
										<>
											Non-Refund Only{" "}
											<Icons.ArrowDownLeft
												rotate="90"
												fontWeight={"semibold"}
											/>
										</>
									)}{" "}
								</Button>
							</Link>
						) : null}
					</Inline>
				)}
			>
				{({ searchParams, setSearchParams }) => (
					<>
						<Tabs>
							<TabsList>
								<TabLink
									params={searchParams}
									setParams={setSearchParams}
									extraParams={extraParams}
									status="future_due"
								>
									Upcoming
								</TabLink>
								<TabLink
									params={searchParams}
									setParams={setSearchParams}
									extraParams={extraParams}
									status="recent_overdue"
								>
									Past 7 Days
								</TabLink>
								<TabLink
									params={searchParams}
									setParams={setSearchParams}
									extraParams={extraParams}
									status="unverified"
								>
									Unverified
								</TabLink>
								<TabLink
									params={searchParams}
									setParams={setSearchParams}
									extraParams={extraParams}
									status="paid"
								>
									Paid
								</TabLink>
								<TabLink
									params={searchParams}
									setParams={setSearchParams}
									extraParams={extraParams}
									status="overdue"
								>
									Overdue
								</TabLink>
								<TabLink
									params={searchParams}
									setParams={setSearchParams}
									extraParams={extraParams}
									status="all"
								>
									All
								</TabLink>
							</TabsList>
							<TabContent overflow="auto" style={{ minHeight: "50vh" }}>
								<List
									params={params}
									pageKey={credit ? "incoming-payments" : "outgoing-payments"}
									setParams={setParams}
									extraParams={extraParams}
									hideContact={hideContact}
								/>
							</TabContent>
						</Tabs>
					</>
				)}
			</Search>
		</>
	)
}

type TStatus =
	| "all"
	| "overdue"
	| "recent_overdue"
	| "future_due"
	| "paid"
	| "unverified"

function TabLink({
	params,
	setParams,
	children,
	showCount,
	extraParams,
	status,
}: {
	params: IFilters
	setParams: (params: IFilters) => void
	children: React.ReactNode
	showCount?: boolean
	extraParams?: Record<string, unknown>
	status: TStatus
}) {
	return (
		<Box as="li" className={params.status === status ? "active" : ""}>
			<a
				href={`#${status}`}
				onClick={(e) => {
					e.preventDefault()
					setParams({
						...params,
						status,
					})
				}}
			>
				{children}{" "}
				{showCount ? (
					<Count {...params} {...extraParams} status={status} />
				) : null}
			</a>
		</Box>
	)
}

function PaymentsMobileView({
	instalments,
	onChange,
}: {
	instalments: IInstalment[]
	onChange?: () => void
}) {
	return (
		<Stack gap="4">
			{instalments.map((instalment) => {
				const {
					id,
					currency,
					amount,
					due_at,
					due_at_local,
					paid_at,
					payment,
					is_overdue,
				} = instalment

				return (
					<Stack
						key={id}
						padding="4"
						bgColor="default"
						gap="3"
						boxShadow="base"
						borderRadius="md"
						overflow="hidden"
					>
						<Inline justifyContent="between">
							<InstalmentItemInDialog
								key={id}
								instalmentId={id}
								onChange={onChange}
								showSiblings
							>
								{({ show }) => (
									<Button inline onClick={() => show()}>
										<Money
											amount={amount}
											currency={currency}
											showCurrency
											fontSize="lg"
											color="accent"
											fontWeight="semibold"
										/>
									</Button>
								)}
							</InstalmentItemInDialog>

							<Text>
								Due:{" "}
								<RelativeTime
									timestamp={due_at}
									localTimestamp={due_at_local}
									minUnit="day"
								/>
							</Text>
						</Inline>

						<Stack justifyContent="between">
							{payment && (
								<Box>
									<InstalmentContact instalment={instalment} />
								</Box>
							)}
						</Stack>

						<Inline justifyContent="between" alignItems="center" gap="4">
							<Box>
								{paid_at ? (
									<Badge success outlined>
										Paid{" "}
										<RelativeTime value={utcTimestampToLocalDate(paid_at)} />
									</Badge>
								) : is_overdue ? (
									<Badge warning outlined>
										Overdue
									</Badge>
								) : null}
							</Box>

							<Inline gap="4">
								<AddCommentInDialog
									commentableType="instalments"
									commentableId={id}
									canMessageCustomer={payment?.paymentable_type === "trips"}
									onChange={onChange}
									level="tertiary"
									size="sm"
								>
									<Icons.Annotation size="4" />
								</AddCommentInDialog>
								{!paid_at && (
									<Box>
										<LogTransaction
											instalment={instalment}
											onChange={onChange}
										/>
									</Box>
								)}
							</Inline>
						</Inline>
					</Stack>
				)
			})}
		</Stack>
	)
}

function PaidInstalmentsMobileView({
	instalments,
	onChange,
}: {
	instalments: IInstalment[]
	onChange?: () => void
}) {
	return (
		<Stack gap="4">
			{instalments.map((instalment) => {
				const {
					id,
					amount,
					paid_at,
					payment,
					currency,
					reference_id,
					debit_accounts,
					credit_accounts,
					verified_at,
					can_verify_payment,
				} = instalment

				return (
					<Stack
						key={id}
						gap="3"
						padding="4"
						borderRadius="md"
						bgColor="default"
						boxShadow="base"
					>
						<Inline gap="2" justifyContent="between" alignItems="center">
							<InstalmentItemInDialog
								key={id}
								instalmentId={id}
								onChange={onChange}
								showSiblings
							>
								{({ show }) => (
									<Button inline onClick={() => show()}>
										<Text
											as="span"
											fontSize="lg"
											color="accent"
											fontWeight="semibold"
										>
											<Money amount={amount} currency={currency} showCurrency />
										</Text>
									</Button>
								)}
							</InstalmentItemInDialog>

							<Stack>
								{paid_at && (
									<Text fontSize="sm">
										Paid:{" "}
										<RelativeTime value={utcTimestampToLocalDate(paid_at)} />
									</Text>
								)}
								{reference_id && (
									<Box
										style={{ maxWidth: "150px" }}
										marginTop="1"
										title={reference_id}
									>
										<Text fontSize="sm" color="muted" textOverflow="truncate">
											Ref: {reference_id}
										</Text>
									</Box>
								)}
							</Stack>
						</Inline>
						{payment && (
							<Box>
								<InstalmentContact instalment={instalment} />
							</Box>
						)}

						<Inline alignItems="center" justifyContent="between" gap="4">
							<Inline alignItems="center" justifyContent="between" gap="3">
								<Box marginBottom="2">
									<Text fontSize="xs" color="muted">
										Debit Accounts
									</Text>
									{debit_accounts?.map((a, i) => (
										<Fragment key={a.id}>
											{i > 0 ? " + " : null}
											<Link
												to={generatePath("/accounting/accounts/:accountId", {
													accountId: String(a.id),
												})}
												textDecoration={{ hover: "underline" }}
											>
												{a.name}
											</Link>
										</Fragment>
									))}
								</Box>
								<Box>
									<Icons.ArrowLeft color="default" rotate="180" />
								</Box>
								<Box marginBottom="2">
									<Text fontSize="xs" color="muted">
										Credit Accounts
									</Text>
									{credit_accounts?.map((a, i) => (
										<Fragment key={a.id}>
											{i > 0 ? " + " : null}
											<Link
												to={generatePath("/accounting/accounts/:accountId", {
													accountId: String(a.id),
												})}
												textDecoration={{ hover: "underline" }}
											>
												{a.name}
											</Link>
										</Fragment>
									))}
								</Box>
							</Inline>

							<Inline justifyContent="end" flexWrap="wrap" gap="1">
								{instalment.pg_invoice?.paid_at && !instalment.verified_at ? (
									<Stack gap="1" alignItems="end">
										<Box>
											<Badge success>
												Paid <Icons.LightningBoltSolid /> PG
											</Badge>
										</Box>
										<Inline
											alignItems="center"
											gap="1"
											color="muted"
											title="Auto Verification pending for Settlements"
										>
											<Icons.Clock size="4" />
											<Text fontSize="xs">Auto Verifying</Text>
										</Inline>
									</Stack>
								) : null}
								{verified_at ? (
									<Stack gap={"2"}>
										<Badge success outlined>
											<Icons.OkCircleSolid /> Verified
											{instalment.pg_invoice?.paid_at ? (
												<>
													{" "}
													<Icons.LightningBoltSolid /> PG
												</>
											) : null}
										</Badge>

										<Time
											timestamp={instalment.verified_at}
											fontSize="sm"
											color="muted"
										>
											{instalment?.verified_by?.name && (
												<Text
													cursor="pointer"
													fontSize={"sm"}
													textAlign={"center"}
												>
													By: {instalment?.verified_by?.name}
												</Text>
											)}
										</Time>
									</Stack>
								) : can_verify_payment ? (
									<VerifyPaymentInDialog
										instalment={instalment}
										onSuccess={onChange}
									>
										{({ verify }) => (
											<Button
												onClick={() => verify()}
												status="warning"
												size="sm"
											>
												<Icons.BadgeCheckSolid /> Verify
											</Button>
										)}
									</VerifyPaymentInDialog>
								) : null}
							</Inline>
						</Inline>
					</Stack>
				)
			})}
		</Stack>
	)
}

function List({
	extraParams,
	pageKey = "payments-list",
	params,
	setParams,
	hideContact,
}: {
	extraParams?: Record<string, unknown>
	pageKey?: string
	params: IFilters
	setParams: (params: IFilters) => void
	hideContact?: boolean
}) {
	const { status } = params
	const showDueStatus = status !== "paid"
	const showOverdueStatus = status !== "overdue" && status !== "recent_overdue"

	const { xs } = useBreakpoints()

	return (
		<ListView<IInstalment, IFilters>
			pageKey={pageKey + queryToSearch(extraParams)}
			params={params}
			fetch={async (xhr, params) => {
				const filters = filtersToAPIParams(params)

				return instalmentsXHR(xhr)
					.getInstallmentsList({
						...extraParams,
						...filters,
						hotels: filters.hotels ?? extraParams?.hotels,
						transportServiceProviders:
							filters.transportServiceProviders ??
							extraParams?.transportServiceProviders,
						trip_source_ids:
							filters.trip_source_ids ?? extraParams?.trip_source_ids,
					})
					.then((resp) => resp)
			}}
			onPageChange={(page) => setParams({ ...params, page })}
			actions={({ total }) => {
				const filters = filtersToAPIParams(params)
				return total && total < 10000 ? (
					<TemporaryStorage
						initUrl={"/instalments/download"}
						initParams={{
							...extraParams,
							...filters,
							timezone_offset: config.timezoneOffset,
							hotels: filters.hotels ?? extraParams?.hotels,
							transportServiceProviders:
								filters.transportServiceProviders ??
								extraParams?.transportServiceProviders,
							trip_source_ids:
								filters.trip_source_ids ?? extraParams?.trip_source_ids,
						}}
					>
						{({ generate, url, message, reset, isGenerating }) =>
							url ? (
								<Button
									as="a"
									href={url}
									target="_blank"
									download
									level="primary"
									size="sm"
									onClick={() => reset()}
								>
									{message}
								</Button>
							) : (
								<Button size="sm" onClick={generate} disabled={isGenerating}>
									{message || (
										<>
											<Icons.DocumentDownload /> Download
										</>
									)}
								</Button>
							)
						}
					</TemporaryStorage>
				) : null
			}}
		>
			{({ items: instalments, refresh }) =>
				status === "paid" || status === "unverified" ? (
					xs ? (
						<PaidInstalmentsMobileView
							instalments={instalments}
							onChange={refresh}
						/>
					) : (
						<PaidInstalmentsList instalments={instalments} onChange={refresh} />
					)
				) : xs ? (
					<PaymentsMobileView instalments={instalments} onChange={refresh} />
				) : (
					<Table
						bordered
						striped
						responsive
						headers={["Amount", "Due Date"]
							.concat(hideContact ? [] : ["Contact"])
							.concat(["Comments", "Actions"])}
						rows={instalments.map((instalment) => {
							const {
								currency,
								amount,
								due_at,
								due_at_local,
								paid_at,
								payment,
								is_overdue,
								comments,
								pg_invoice,
							} = instalment
							return (
								[
									<Box>
										<InstalmentItemInDialog
											key={instalment.id}
											instalmentId={instalment.id}
											onChange={() => refresh()}
											showSiblings
										>
											{({ show }) => (
												<Button inline onClick={() => show()}>
													<Text as="span" fontSize="md" color="accent">
														<Money
															amount={amount}
															currency={currency}
															showCurrency
														/>
													</Text>
												</Button>
											)}
										</InstalmentItemInDialog>
										{showDueStatus ? (
											<Box fontSize="sm">
												{paid_at ? (
													<Badge success outlined>
														Paid{" "}
														<RelativeTime
															value={utcTimestampToLocalDate(paid_at)}
														/>
													</Badge>
												) : is_overdue && showOverdueStatus ? (
													<Badge warning outlined>
														Overdue
													</Badge>
												) : null}
											</Box>
										) : null}
									</Box>,
									<Box>
										<RelativeTime
											timestamp={due_at}
											localTimestamp={due_at_local}
											minUnit="day"
										/>
									</Box>,
								] as Array<React.ReactNode>
							)
								.concat(
									hideContact
										? []
										: [
												payment ? (
													<InstalmentContact instalment={instalment} />
												) : null,
											]
								)
								.concat([
									<Box>
										{comments?.length ? (
											<Box fontSize="sm">
												{comments.map((c) => (
													<CommentableCommentItem
														key={c.id}
														comment={c}
														onChange={() => refresh()}
													/>
												))}
											</Box>
										) : null}
										<AddCommentInDialog
											commentableType="instalments"
											commentableId={instalment.id}
											canMessageCustomer={payment?.paymentable_type === "trips"}
											onChange={() => refresh()}
											inline
											size="sm"
										>
											Add Comment
										</AddCommentInDialog>
									</Box>,
									<Box>
										{!paid_at ? (
											pg_invoice ? (
												!pg_invoice.unverified_successful_transactions_count ? (
													<CopyToClipboard>
														{({ copyTextToClipboard }) => (
															<CopyToClipboardButton
																onClick={() =>
																	copyTextToClipboard(pg_invoice.link)
																}
																size="sm"
															>
																<Icons.ClipboardCopy /> Payment Link
															</CopyToClipboardButton>
														)}
													</CopyToClipboard>
												) : (
													<Badge
														warning
														title="Payment was successful but the transaction could not be verified."
													>
														<Icons.Clock /> Processing{" "}
														<Icons.LightningBoltSolid /> PG
													</Badge>
												)
											) : (
												<LogTransaction
													instalment={instalment}
													onChange={() => refresh()}
												/>
											)
										) : null}
									</Box>,
								])
						})}
					/>
				)
			}
		</ListView>
	)
}

function PaidInstalmentsList({
	instalments,
	onChange,
}: {
	instalments: Array<IInstalment>
	onChange?: () => void
}) {
	return (
		<Table
			bordered
			striped
			responsive
			headers={[
				"Amount",
				"Paid On",
				"Debit Acc.",
				"Credit Acc.",
				"Contact",
				"",
			]}
			rows={instalments.map((instalment) => {
				const {
					amount,
					paid_at,
					payment,
					currency,
					reference_id,
					debit_accounts,
					credit_accounts,
				} = instalment
				return [
					<Box>
						<InstalmentItemInDialog
							key={instalment.id}
							instalmentId={instalment.id}
							onChange={onChange}
							showSiblings
						>
							{({ show }) => (
								<Button inline onClick={() => show()}>
									<Text as="span" fontSize="md" color="accent">
										<Money amount={amount} currency={currency} showCurrency />
									</Text>
								</Button>
							)}
						</InstalmentItemInDialog>
					</Box>,
					<Box>
						{paid_at ? (
							<RelativeTime value={utcTimestampToLocalDate(paid_at)} />
						) : null}
						{reference_id ? (
							<Box
								style={{ maxWidth: "150px" }}
								marginTop="1"
								title={reference_id}
							>
								<Text fontSize="sm" color="muted" textOverflow="truncate">
									Ref: {reference_id}
								</Text>
							</Box>
						) : null}
					</Box>,
					debit_accounts?.map((a, i) => (
						<Fragment key={a.id}>
							{i > 0 ? " + " : null}
							<Link
								to={generatePath("/accounting/accounts/:accountId", {
									accountId: String(a.id),
								})}
								textDecoration={{ hover: "underline" }}
							>
								{a.name}
							</Link>
						</Fragment>
					)),
					credit_accounts?.map((a, i) => (
						<Fragment key={a.id}>
							{i > 0 ? " + " : null}
							<Link
								to={generatePath("/accounting/accounts/:accountId", {
									accountId: String(a.id),
								})}
								textDecoration={{ hover: "underline" }}
							>
								{a.name}
							</Link>
						</Fragment>
					)),
					payment ? <InstalmentContact instalment={instalment} /> : null,
					<Inline
						key={instalment.id}
						gap="2"
						flexWrap={"wrap"}
						justifyContent="end"
					>
						{instalment.pg_invoice?.paid_at && !instalment.verified_at ? (
							<Stack gap="1" alignItems="end">
								<Box>
									<Badge success title="Collection using Payment Gateway">
										Paid <Icons.LightningBoltSolid /> PG
									</Badge>
								</Box>
								<Inline
									alignItems="center"
									justifyContent="end"
									gap="1"
									color="muted"
									title="Auto Verification pending for Settlements"
								>
									<Icons.Clock size="4" />
									<Text fontSize="xs">Auto&nbsp;Verifying</Text>
								</Inline>
							</Stack>
						) : null}
						{instalment.verified_at ? (
							<Stack gap={"1"}>
								<Box>
									<Badge success outlined>
										<Icons.OkCircleSolid /> Verified
										{instalment.pg_invoice?.paid_at ? (
											<>
												{" "}
												<Icons.LightningBoltSolid /> PG
											</>
										) : null}
									</Badge>
								</Box>
								<Time
									timestamp={instalment.verified_at}
									fontSize="sm"
									color="muted"
								>
									{instalment?.verified_by?.name && (
										<Text cursor="pointer" fontSize={"sm"} textAlign={"right"}>
											by {instalment?.verified_by?.name}
										</Text>
									)}
								</Time>
							</Stack>
						) : instalment.can_verify_payment ? (
							<Box>
								<VerifyPaymentInDialog
									instalment={instalment}
									onSuccess={onChange}
								>
									{({ verify }) => (
										<Button onClick={() => verify()} status="warning" size="sm">
											<Icons.BadgeCheckSolid /> Verify
										</Button>
									)}
								</VerifyPaymentInDialog>
							</Box>
						) : null}
					</Inline>,
				]
			})}
		/>
	)
}

function Count({ limit, page, ...params }: IFilters) {
	const xhr = useXHR()
	const apiParams = filtersToAPIParams(params)
	const { data, error } = useSWR<IListResponse<IInstalment>>(
		`/instalments-count${queryToSearch(apiParams)}`,
		() =>
			xhr
				.get("/instalments", {
					params: {
						...apiParams,
						limit: 1,
					},
				})
				.then((resp) => resp.data)
	)
	if (!data || error) return null
	const { meta } = data
	return <Badge>{meta.total}</Badge>
}

export interface IFilters extends TSearchParams {
	due_after?: string | Date
	due_before?: string | Date
	page?: number
	limit?: number
	status: TStatus
	hide_on_hold?: boolean
	verified?: 1
	paymentable_types?: Array<{ name: string; id: string }>
	trip_sources?: Array<{ name: string; id: string }>
	is_refund?: { name: string; id: string }
	hotels?: Array<{ name: string; id: string }>
	transport_service_providers?: Array<{ name: string; id: string }>
	trip_destination_ids?: Array<{ name: string; id: string }>
	back?: string
	sales_team?: Array<IUser>
}

export interface IFiltersInLocationQuery extends TSearchParams {
	due_after?: string
	due_before?: string
	page?: number
	status: TStatus
	hide_on_hold?: 1
	verified?: 1
	paymentable_types?: Array<string>
	trip_sources?: Array<string>
	hotels?: Array<string>
	transport_service_providers?: Array<string>
	trip_destination_ids?: Array<string>
	is_refund?: Array<string>
	sales_team?: Array<string>
}

function paramsToLocationQuery(params: IFilters): IFiltersInLocationQuery {
	const {
		q,
		due_after,
		due_before,
		hide_on_hold,
		verified,
		paymentable_types,
		trip_sources,
		is_refund,
		transport_service_providers,
		trip_destination_ids,
		hotels,
		sales_team,
		...otherData
	} = params
	const filters: IFiltersInLocationQuery = {
		...otherData,
		status: otherData.status || "future_due",
	}
	if (q) {
		filters.q = q
	}
	if (due_after) {
		filters.due_after = dateToQuery(due_after)
	}
	if (due_before) {
		filters.due_before = dateToQuery(due_before)
	}
	if (hide_on_hold) {
		filters.hide_on_hold = 1
	}
	if (verified) {
		filters.verified = 1
	}
	if (paymentable_types?.length) {
		filters.paymentable_types = paymentable_types.map(
			(t) => `${t.id}-${t.name}`
		)
	}
	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((s) => `${s.id}-${s.name}`)
	}
	if (is_refund) {
		filters.is_refund = [is_refund.id]
	}
	if (hotels?.length) {
		filters.hotels = hotels.map((h) => `${h.id}-${h.name}`)
	}
	if (transport_service_providers?.length) {
		filters.transport_service_providers = transport_service_providers.map(
			(s) => `${s.id}-${s.name}`
		)
	}
	if (trip_destination_ids?.length) {
		filters.trip_destination_ids = trip_destination_ids.map(
			(s) => `${s.id}-${s.name}`
		)
	}
	if (sales_team?.length) {
		filters.sales_team = sales_team.map((s) => `${s.id}-${s.name}`)
	}

	return filters
}

function locationQueryToParams(query: IFiltersInLocationQuery): IFilters {
	const {
		q,
		due_after,
		due_before,
		hide_on_hold,
		verified,
		paymentable_types,
		trip_sources,
		is_refund,
		transport_service_providers,
		trip_destination_ids,
		hotels,
		sales_team,
		...otherData
	} = query
	const filters: IFilters = {
		...otherData,
		status: otherData.status || "future_due",
	}
	if (q) {
		filters.q = q
	}
	if (due_after) {
		filters.due_after = parseDateFromQuery(due_after)
	}
	if (due_before) {
		filters.due_before = parseDateFromQuery(due_before)
	}
	if (hide_on_hold) {
		filters.hide_on_hold = true
	}
	if (verified) {
		filters.verified = 1
	}

	if (paymentable_types?.length) {
		filters.paymentable_types = paymentable_types.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((s) => {
			const [id, ...name] = s.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (hotels?.length) {
		filters.hotels = hotels.map((s) => {
			const [id, ...name] = s.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (transport_service_providers?.length) {
		filters.transport_service_providers = transport_service_providers.map(
			(s) => {
				const [id, ...name] = s.split("-")
				return {
					id,
					name: name.join("-"),
				} as never as { id: string; name: string }
			}
		)
	}
	if (trip_destination_ids?.length) {
		filters.trip_destination_ids = trip_destination_ids.map((s) => {
			const [id, ...name] = s.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (is_refund?.length) {
		filters.is_refund = is_refund.map((s) => {
			const [id, ...name] = s.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})[0]
	}

	if (sales_team?.length) {
		filters.sales_team = sales_team.map((s) => {
			const [id, ...name] = s.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as IUser
		})
	}
	return filters
}

function Filters({
	paymentContext,
	supplyer_ids,
}: {
	paymentContext: TPaymentContext
	supplyer_ids: number[] | undefined
}) {
	const { value: status } = useFieldValue<IFilters["status"]>("status")
	const hasHoldingEnabled = useHasFeatureFlag("hold_trips")
	return (
		<Stack gap="4">
			{hasHoldingEnabled ? (
				<SwitchInputField label="Hide On-Hold Bookings" name="hide_on_hold" />
			) : null}
			<DateRangePickerField
				label="Due Between"
				fromName="due_after"
				toName="due_before"
			/>

			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext][
				"filter_by_trip_destination_ids"
			] ? (
				<SelectField
					name="trip_destination_ids"
					select={SelectTripDestination}
					label="Destinations"
					fetchOnMount
					multiple
					placeholder="Select destination(s)..."
				/>
			) : null}
			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext][
				"filter_by_paymentable_types"
			] ? (
				<SelectField
					select={SelectPaymentType}
					fetchOnMount
					supplierIds={supplyer_ids}
					label="Payment For"
					name="paymentable_types"
					multiple
				/>
			) : null}

			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext]["filter_by_hotels"] ? (
				<SelectField
					label="Hotels"
					name="hotels"
					select={SelectHotels}
					multiple
					fetchOnMount
				/>
			) : null}
			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext][
				"filter_by_trip_source_ids"
			] ? (
				<SelectField
					label="Trip Sources"
					select={SelectTripSources}
					name="trip_sources"
					multiple
					fetchOnMount
					withRecentlyDisabledWithinDays={30}
				/>
			) : null}
			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext][
				"filter_by_transport_service_providers"
			] ? (
				<SelectField
					select={SelectTransportAndActivitySuppliers}
					name="transport_service_providers"
					label="Suppliers"
					multiple
				/>
			) : null}

			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext]["is_refund"] ? (
				<SelectField
					select={Select}
					label="Payment Type"
					name="is_refund"
					options={refundOptions}
				/>
			) : null}

			{PAYMENT_CONTEXT_PERMISSIONS[paymentContext]["sales_person_filter"] ? (
				<SelectField
					select={SelectTripOwners}
					name="sales_team"
					label="Sales Person"
					multiple
					fetchOnMount
					teamType="sales"
				/>
			) : null}

			{status === "paid" ? (
				<SwitchInputField label="Verified Only" name="verified" />
			) : null}
		</Stack>
	)
}

function filtersToAPIParams(params: IFilters) {
	const {
		due_after,
		due_before,
		status,
		is_refund,
		hide_on_hold,
		verified,
		paymentable_types,
		trip_sources,
		transport_service_providers,
		hotels,
		trip_destination_ids,
		sales_team,
		...otherData
	} = params
	const is_due_in_future = status === "future_due" ? 1 : undefined
	const is_overdue =
		status === "overdue" || status === "recent_overdue" ? 1 : undefined
	const due_after_local = due_after
		? startOf(due_after, "day")
		: is_due_in_future
			? // set the due_after to start of today
				startOf(new Date(), "day")
			: status === "recent_overdue"
				? endOf(subtractUnit(new Date(), 7, "day"), "day")
				: undefined
	const due_before_local = due_before
		? endOf(due_before, "day")
		: is_overdue
			? // set the due_before to end of yesterday
				endOf(subtractUnit(new Date(), 1, "day"), "day")
			: undefined

	return {
		...otherData,
		due_after_local: due_after_local ? dateToQuery(due_after_local) : null,
		due_after: due_after_local ? dateToUTCString(due_after_local) : null,
		due_before_local: due_before_local ? dateToQuery(due_before_local) : null,
		due_before: due_before_local ? dateToUTCString(due_before_local) : null,
		is_due_in_future,
		is_overdue,
		is_due: is_due_in_future || is_overdue,
		is_paid: status === "paid" || status === "unverified" ? 1 : undefined,
		hide_on_hold: hide_on_hold ? 1 : undefined,
		verified:
			status === "unverified"
				? 0
				: status === "paid" && verified
					? 1
					: undefined,
		paymentable_types: paymentable_types?.map((t) => t.id),
		trip_source_ids: trip_sources?.map((t) => t.id),
		is_refund: is_refund?.id,
		transportServiceProviders: transport_service_providers?.map((s) => s.id),
		hotels: hotels?.map((h) => h.id),
		trip_destination_ids: trip_destination_ids?.map((t) => t.id),
		sales_team: sales_team?.map((s) => s.id),
	}
}

type TMultiplePaymentStep =
	| "selectFilter"
	| "selectInstallments"
	| "finalStep"
	| "none"

interface StepConfig {
	next: TMultiplePaymentStep
	previous: TMultiplePaymentStep
}

const STEP_MANAGER: Record<TMultiplePaymentStep, StepConfig> = {
	selectFilter: {
		previous: "none",
		next: "selectInstallments",
	},
	selectInstallments: {
		previous: "selectFilter",
		next: "finalStep",
	},
	finalStep: {
		previous: "selectInstallments",
		next: "none",
	},
	none: {
		next: "selectFilter",
		previous: "none",
	},
}

function LogMultiplePayments({
	children,
	paymentContext,
	onSuccess,
	defaultParamsOfMultipleLog,
}: {
	children: (props: { onLogMultiple: () => void }) => React.ReactNode
	paymentContext: TPaymentContext
	onSuccess: () => void
	defaultParamsOfMultipleLog?: TMultiLogPaymentsFilterFormType
}) {
	const [activeStep, setActiveStep] = useState<TMultiplePaymentStep>("none")
	const [filterValues, setFilterValues] = useState<
		TMultiLogPaymentsFilterFormType | undefined
	>(defaultParamsOfMultipleLog)

	const onChangeFilter = useCallback(
		(values: TMultiLogPaymentsFilterFormType | undefined) =>
			setFilterValues(values || defaultParamsOfMultipleLog),
		[defaultParamsOfMultipleLog]
	)
	useEffect(() => {
		onChangeFilter(defaultParamsOfMultipleLog)
	}, [defaultParamsOfMultipleLog, onChangeFilter])

	const onClose = useCallback(() => {
		setActiveStep("none")
		onChangeFilter(defaultParamsOfMultipleLog)
	}, [defaultParamsOfMultipleLog, onChangeFilter])

	const onNext = useCallback(
		() => setActiveStep((p) => STEP_MANAGER[p].next),
		[]
	)

	const onPrevious = useCallback(
		() => setActiveStep((p) => STEP_MANAGER[p].previous),
		[]
	)

	return (
		<>
			{children({ onLogMultiple: onNext })}
			{activeStep === "selectFilter" ? (
				<MultiLogPaymentsFilter
					filterValues={filterValues}
					step={activeStep}
					onFilterChange={onChangeFilter}
					onNext={onNext}
					onClose={onClose}
					paymentContext={paymentContext}
				/>
			) : activeStep === "selectInstallments" ? (
				<ChooseInstallments
					filterValues={filterValues as TMultiLogPaymentsFilterFormType}
					onFilterChange={onChangeFilter}
					onNext={onNext}
					onPrevious={onPrevious}
					onClose={onClose}
					step={activeStep}
				/>
			) : activeStep === "finalStep" ? (
				<FinalizeMultiInstallments
					onFilterChange={onChangeFilter}
					step={activeStep}
					onPrevious={onPrevious}
					onClose={onClose}
					filterValues={filterValues as TMultiLogPaymentsFilterFormType}
					onSuccess={onSuccess}
				/>
			) : null}
		</>
	)
}

export interface TMultiLogPaymentsFilterFormType {
	currency?: string | null
	paymentable_types?: { name: string; id: string } | null
	due_after?: Date | undefined
	due_before?: Date | undefined
	hotels?: { id: string; name: string } | null
	transportServiceProviders?: { id: string; name: string } | null
	included_ids?: number[] | undefined
	excluded_ids?: number[] | undefined
	isSelectAll?: boolean | undefined
	payment_for?: { id: string; name: string } | null
	trip_id?: { id: string; name: string } | null
	sort?: string
	is_paid?: number | undefined
	is_overdue?: number | undefined
	verified?: number | undefined
	trip_destination_ids?: Array<{ id: string; name: string }> | null
}

function MultiLogPaymentsFilter({
	step,
	filterValues,
	onNext,
	onClose,
	onFilterChange,
	paymentContext,
}: {
	step: TMultiplePaymentStep
	filterValues: TMultiLogPaymentsFilterFormType | undefined
	onNext: () => void
	onClose: () => void
	onFilterChange: (value: TMultiLogPaymentsFilterFormType | undefined) => void
	paymentContext: TPaymentContext
}) {
	const initialValues = useMemo<TMultiLogPaymentsFilterFormType>(() => {
		const {
			currency,
			paymentable_types,
			due_after,
			due_before,
			transportServiceProviders,
			hotels,
			payment_for,
			trip_id,
			trip_destination_ids,
		} = filterValues || {}
		return {
			currency: currency,
			paymentable_types: paymentable_types || null,
			due_after: due_after,
			due_before: due_before,
			payment_for: payment_for
				? payment_for
				: transportServiceProviders
					? { id: "suppliers", name: "Suppliers" }
					: hotels
						? { id: "hotel_bookings", name: "Direct Hotel" }
						: undefined,
			transportServiceProviders: transportServiceProviders,
			hotels: transportServiceProviders ? undefined : hotels,
			trip_id: trip_id,
			trip_destination_ids,
		}
	}, [filterValues])

	return (
		<Dialog
			lg
			onClose={onClose}
			open={step === "selectFilter"}
			title="Log Multiple Payments"
		>
			<Form<TMultiLogPaymentsFilterFormType>
				initialValues={initialValues}
				validate={validateFormValues(
					Validator.object().shape({
						currency: Validator.mixed().required("Please select the currency"),
						payment_for:
							paymentContext === "outgoing"
								? Validator.mixed().required("Please select the payment for")
								: Validator.mixed().notRequired(),
						transportServiceProviders: Validator.mixed().when("payment_for", {
							is: (val: { id: string }) => val?.id === "suppliers",
							then: (schema) =>
								schema.required("Please select at least one supplier"),
							otherwise: (schema) => schema.notRequired(),
						}),
						hotels: Validator.mixed().when("payment_for", {
							is: (val: { id: string }) => val?.id === "hotel_bookings",
							then: (schema) => schema.required("Please select a hotel"),
							otherwise: (schema) => schema.notRequired(),
						}),
					})
				)}
				onSubmit={withServerErrors(async (values) => {
					onFilterChange(values)
					onNext()
				})}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, form }) => (
					<form onSubmit={handleSubmit}>
						<DialogBody>
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack>
										<Heading fontSize={"lg"}>Select Filters</Heading>
										<Text fontSize={"base"} color="muted">
											Select all required filters to continue with logging
											multiple payments.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Inline flexWrap={"wrap"} gap="2">
										{paymentContext === "outgoing" ? (
											<>
												<SelectField
													select={Select}
													label="Supplier Type"
													name="payment_for"
													options={supplierTypeOptions}
													onChange={(value, name) => {
														form.batch(() => {
															form.change(name as never, value as never)
															form.change(
																`transportServiceProviders` as never,
																undefined
															)
															form.change(`hotels` as never, undefined)
															form.change("paymentable_types", undefined)
														})
													}}
												/>
												<GetFieldValue<{ id: string }> name="payment_for">
													{({ value: payment_for }) => {
														return payment_for?.id === "suppliers" ? (
															<SelectField
																select={SelectTransportAndActivitySuppliers}
																name="transportServiceProviders"
																label="Suppliers"
																fetchOnMount
																onChange={(value, name) => {
																	form.batch(() => {
																		form.change(name as never, value as never)
																		form.change(`hotels` as never, undefined)
																		form.change("paymentable_types", undefined)
																		form.change("currency", undefined)
																	})
																}}
															/>
														) : payment_for?.id === "hotel_bookings" ? (
															<SelectField
																select={SelectSelfBookHotel}
																name={`hotels`}
																creatable
																label="Direct Hotels"
																multiple={false}
																onChange={(value, name) => {
																	form.batch(() => {
																		form.change(name as never, value as never)
																		form.change(
																			`transportServiceProviders` as never,
																			undefined
																		)
																		form.change("paymentable_types", undefined)
																		form.change("currency", undefined)
																	})
																}}
															/>
														) : null
													}}
												</GetFieldValue>
											</>
										) : null}

										<GetFieldValue<{ id: number }> name="hotels">
											{({ value: hotelId }) => {
												return (
													<GetFieldValue<{
														id: number
													}> name="transportServiceProviders">
														{({ value: transportServiceProvider }) => {
															const supplierIds = transportServiceProvider?.id
																? transportServiceProvider.id
																: filterValues?.transportServiceProviders?.id
																	? Number(
																			filterValues.transportServiceProviders.id
																		)
																	: undefined
															return (
																<>
																	<SelectField
																		key={`${supplierIds}-${hotelId?.id || filterValues?.hotels?.id}`}
																		name="currency"
																		select={SelectPendingPaymentCurrencies}
																		label="Currency"
																		fetchOnMount
																		setDefaultCurrency={(currency) => {
																			form.change("currency", currency)
																		}}
																		suppliersIds={supplierIds?.toString()}
																		hotelIds={
																			!supplierIds
																				? hotelId.id?.toString() ||
																					filterValues?.hotels?.id?.toString()
																				: undefined
																		}
																	/>
																	{supplierIds ? (
																		<SelectField
																			key={`${supplierIds}`}
																			select={SelectPaymentType}
																			fetchOnMount
																			supplierIds={supplierIds}
																			label="Payment For"
																			secondaryLabel="optional"
																			name="paymentable_types"
																			searchable={false}
																			multiple={false}
																		/>
																	) : null}
																</>
															)
														}}
													</GetFieldValue>
												)
											}}
										</GetFieldValue>

										<Box style={{ maxWidth: "250px" }}>
											<DateRangePickerField
												secondaryLabel="optional"
												label="Due Between"
												fromName="due_after"
												toName="due_before"
											/>
										</Box>
										{paymentContext === "outgoing" ||
										paymentContext === "suppliers" ? (
											<SelectField
												name="trip_destination_ids"
												select={SelectTripDestination}
												label="Destinations"
												fetchOnMount
												multiple
												placeholder="Select destination(s)..."
											/>
										) : null}
										<SelectField
											select={SelectTrips}
											label="Trip"
											secondaryLabel="optional"
											name="trip_id"
											multiple={false}
											width={"full"}
											style={{ width: "200px" }}
										/>
									</Inline>
								</Col>
							</Grid>
						</DialogBody>
						<DialogFooter>
							<Button level="primary" type="submit">
								Next
							</Button>
							<Button onClick={onClose} level="tertiary">
								Cancel
							</Button>
						</DialogFooter>
					</form>
				)}
			</Form>
		</Dialog>
	)
}

type ICursorMeta = IInstallmentsCursorResponse["meta"]
const paymentTnxType = "debit" // in future if we want to support credit payments we can make this dynamic

function ChooseInstallments({
	step,
	onFilterChange,
	filterValues,
	onNext,
	onPrevious,
	onClose,
}: {
	step: TMultiplePaymentStep
	onFilterChange: (values: TMultiLogPaymentsFilterFormType | undefined) => void
	filterValues: TMultiLogPaymentsFilterFormType
	onNext: () => void
	onPrevious: () => void
	onClose: () => void
}) {
	const [isSelectAll, setIsSelectAll] = useState<boolean>(
		filterValues.isSelectAll || false
	)
	const [sortBy, setSortBy] = useState<string | undefined>(
		filterValues.sort || "due_date"
	)
	const [totalInstallmentAmmount, setInstallmentTotalAmount] = useState(0)
	const [checkedInstallmentIds, setCheckedInstallmentIds] = useState<number[]>(
		[]
	)
	const xhr = useXHR()
	// initialize selected installments it works for back  navigation
	useEffect(() => {
		if (filterValues.included_ids || filterValues.excluded_ids) {
			setIsSelectAll(filterValues.isSelectAll || false)
			setCheckedInstallmentIds([
				...(filterValues.included_ids || filterValues.excluded_ids || []),
			])
		}
	}, [filterValues])

	const onFirstPageMeta = useCallback((meta: ICursorMeta) => {
		if (!meta?.prev_cursor) {
			const totalAmountFromMeta =
				Array.isArray(meta?.total_amount_money) &&
				meta.total_amount_money.length > 0
					? meta.total_amount_money[0]
					: undefined
			if (
				totalAmountFromMeta &&
				typeof totalAmountFromMeta.amount !== "undefined"
			) {
				const formatMoney = formatMoneyByDecimal({
					...totalAmountFromMeta,
					amount: Number(totalAmountFromMeta.amount),
				})
				setInstallmentTotalAmount(Number(formatMoney))
			} else {
				setInstallmentTotalAmount(0)
			}
		}
	}, [])

	function onSubmit() {
		onNext()
		onFilterChange({
			...filterValues,
			included_ids: isSelectAll ? undefined : checkedInstallmentIds,
			excluded_ids: isSelectAll ? checkedInstallmentIds : undefined,
			isSelectAll,
			sort: sortBy,
		})
	}

	const onFetch = useCallback(
		async ({
			cursor,
			limit,
		}: {
			cursor?: string
			limit: number
		}): Promise<{ data: IInstalment[]; meta: ICursorMeta }> => {
			const {
				currency,
				included_ids,
				excluded_ids,
				isSelectAll,
				transportServiceProviders,
				hotels,
				payment_for,
				paymentable_types,
				trip_id,
				due_after,
				due_before,
				sort,
				trip_destination_ids,
				...rest
			} = filterValues

			const due_after_local = due_after ? startOf(due_after, "day") : undefined
			const due_before_local = due_before ? endOf(due_before, "day") : undefined
			const res = await instalmentsXHR(xhr).getInstallmentsWithCursorPagination(
				{
					...rest,
					limit,
					cursor: cursor,
					sort: sortBy,
					currencies: [currency || ""],
					include: !cursor ? "total_amount_money" : undefined,
					transportServiceProviders: transportServiceProviders?.id
						? [transportServiceProviders?.id]
						: [],
					hotels: [hotels?.id],
					paymentable_types: [paymentable_types?.id],
					hide_on_hold: 1,
					is_due: 1,
					is_debit: paymentTnxType === "debit" ? 1 : 0,
					trip_id: trip_id?.id,
					due_after_local: due_after_local
						? dateToQuery(due_after_local)
						: null,
					due_after: due_after_local ? dateToUTCString(due_after_local) : null,
					due_before_local: due_before_local
						? dateToQuery(due_before_local)
						: null,
					due_before: due_before_local
						? dateToUTCString(due_before_local)
						: null,
					only_direct_hotel_payments: hotels?.id ? 1 : undefined,
					trip_destination_ids: trip_destination_ids?.map((t) => t.id),
				}
			)
			return {
				data: res.data,
				meta: res.meta,
			}
		},
		[filterValues, sortBy, xhr]
	)

	function onSelectAll() {
		setIsSelectAll(true)
		setCheckedInstallmentIds([])
	}

	function onSortChange(val: string | undefined) {
		setSortBy(val)
	}
	return (
		<Dialog
			lg
			onClose={onClose}
			open={step === "selectInstallments"}
			title="Log Multiple Payments"
		>
			<InfiniteScroll<IInstalment, ICursorMeta>
				limit={10}
				resetKey={sortBy || ""}
				fetcher={onFetch}
				onFirstPageMeta={onFirstPageMeta}
			>
				{({
					items: instalments,
					isLoading,
					retry,
					errorMessage,
					containerRef,
				}) => {
					return (
						<ChooseInstallmentsTable
							instalments={instalments}
							isLoading={isLoading}
							retry={retry}
							errorMessage={errorMessage || ""}
							containerRef={containerRef}
							checkedInstallmentIds={checkedInstallmentIds}
							isSelectAll={isSelectAll}
							totalInstallmentAmmount={totalInstallmentAmmount}
							onPrevious={onPrevious}
							onSelectAll={onSelectAll}
							onSubmit={onSubmit}
							onSortChange={onSortChange}
							setCheckedInstallmentIds={setCheckedInstallmentIds}
							sortBy={sortBy}
							currency={filterValues.currency || ""}
						/>
					)
				}}
			</InfiniteScroll>
		</Dialog>
	)
}

function ChooseInstallmentsTable({
	instalments,
	isLoading,
	retry,
	errorMessage,
	containerRef,
	checkedInstallmentIds,
	isSelectAll,
	totalInstallmentAmmount,
	onPrevious,
	onSelectAll,
	onSubmit,
	onSortChange,
	setCheckedInstallmentIds,
	sortBy,
	currency,
}: {
	instalments: IInstalment[]
	isLoading: boolean
	retry: () => void
	errorMessage: string
	containerRef: React.RefObject<HTMLDivElement | null>
	checkedInstallmentIds: number[]
	isSelectAll: boolean
	totalInstallmentAmmount: number
	onPrevious: () => void
	onSelectAll: () => void
	onSubmit: () => void
	onSortChange: (val: string | undefined) => void
	setCheckedInstallmentIds: Dispatch<SetStateAction<number[]>>
	sortBy: string | undefined
	currency: string
}) {
	const { selectedTotalAmount, hasMinimumOneSelected } = useMemo(() => {
		const selectedCount = checkedInstallmentIds.length

		// No selection
		if (selectedCount === 0) {
			return {
				selectedTotalAmount: isSelectAll ? totalInstallmentAmmount : 0,
				hasMinimumOneSelected:
					isSelectAll && instalments.length > 0 // if selectAll, need at least 1 unselected?
						? true
						: false,
			}
		}

		let sum = 0
		instalments.forEach((instalment) => {
			if (checkedInstallmentIds.includes(instalment.id))
				sum += Number(instalment.amount)
		})

		const selectedTotalAmount = isSelectAll
			? totalInstallmentAmmount - sum
			: sum
		const hasMinimumOneSelected =
			(isSelectAll && selectedCount < instalments.length) ||
			(!isSelectAll && selectedCount > 0)

		return { selectedTotalAmount, hasMinimumOneSelected }
	}, [instalments, isSelectAll, checkedInstallmentIds, totalInstallmentAmmount])

	const sortOptions: TSortOptions = useMemo(
		() => [
			{
				label: "Due Date Ascending",
				name: "due_date",
				type: "date",
				order: "asc",
				removeOrderSuffix: true,
			},
			{
				label: "Due Date Descending",
				name: "due_date",
				type: "date",
				order: "desc",
				removeOrderSuffix: true,
			},
		],
		[]
	)
	return (
		<>
			<DialogBody>
				<Stack gap="4">
					{instalments.length ? (
						<Stack gap="2">
							{!isSelectAll ? (
								<Inline
									justifyContent={"end"}
									cursor="pointer"
									as="label"
									alignItems="center"
									gap="2"
								>
									<CheckboxInput
										checked={isSelectAll}
										onChange={onSelectAll}
										name="selected_all"
										id="selected_all"
									/>
									<Box htmlFor="selected_all" as="label">
										Select All
									</Box>
								</Inline>
							) : null}

							<Stack borderWidth="1" borderColor="muted" rounded="lg">
								{errorMessage ? (
									<RefreshComponent message={errorMessage} onRefresh={retry} />
								) : null}

								<Inline paddingX="4" paddingY={"4"} justifyContent={"between"}>
									<Heading fontSize={"lg"}>Select payments</Heading>
									<SortOptions
										onChange={onSortChange}
										value={sortBy}
										options={sortOptions}
									/>
								</Inline>

								<Box paddingX="4">
									<Box
										ref={containerRef}
										style={{
											maxHeight: 400,
											overflowY: "auto",
										}}
									>
										<BulkPaymentInstalmentTable
											instalments={instalments}
											isSelectAll={isSelectAll}
											checkedIds={checkedInstallmentIds}
											onCheckedIdsChange={setCheckedInstallmentIds}
											showCheckbox
											showContact
										/>

										{isLoading ? (
											<SpinnerWithMessage
												message={"Loading more installments..."}
											/>
										) : null}
									</Box>
								</Box>

								<Box padding={"4"} alignItems={"center"} roundedTop="lg">
									{hasMinimumOneSelected ? (
										<Inline gap="2" alignItems="center">
											<Text>Total: </Text>
											<Text as="span" fontSize="md" color="accent">
												<Money
													amount={selectedTotalAmount}
													currency={currency}
													showCurrency
												/>
											</Text>
										</Inline>
									) : null}
								</Box>
							</Stack>
						</Stack>
					) : isLoading ? (
						<SpinnerWithMessage message="Fetching data" />
					) : (
						<Stack justifyContent={"center"} alignItems={"center"}>
							<Box maxWidth={"2xl"}>
								<Stack
									gap={"2"}
									justifyContent={"center"}
									alignItems={"center"}
								>
									<Icons.Info color="muted" size={"10"} />
									<Text color={"muted"} textAlign={"center"} fontSize={"md"}>
										No payments match the selected filters. Try adjusting the
										filters or removing some conditions to see available
										payments.
									</Text>
									<Button level="secondary" onClick={onPrevious}>
										Select Filters
									</Button>
								</Stack>
							</Box>
						</Stack>
					)}
				</Stack>
			</DialogBody>
			<DialogFooter>
				{!instalments.length && !isLoading ? null : (
					<Button
						onClick={() => {
							if (!hasMinimumOneSelected) {
								window.alert("Please select at least one payments to proceed.")
							} else {
								onSubmit()
							}
						}}
						level="primary"
						type="button"
					>
						Next
					</Button>
				)}
				<Button onClick={onPrevious} level="tertiary">
					Back
				</Button>
			</DialogFooter>
		</>
	)
}

function FinalizeMultiInstallments({
	step,
	onFilterChange,
	onPrevious,
	onClose,
	filterValues,
	onSuccess,
}: {
	step: TMultiplePaymentStep
	onFilterChange: (values: TMultiLogPaymentsFilterFormType) => void
	onPrevious: () => void
	onClose: () => void
	filterValues: TMultiLogPaymentsFilterFormType
	onSuccess: () => void
}) {
	const chunkSize = 10
	const xhr = useXHR()
	const [requestStatus, setRequestStatus] = useState<ApiRequestStatus>({
		isLoading: false,
		errorMessage: "",
	})
	const [installentList, setInstallmentList] = useState<IInstalment[]>([])
	const {
		currency,
		transportServiceProviders,
		isSelectAll,
		paymentable_types,
		hotels,
		payment_for,
		trip_id,
		due_before,
		due_after,
		trip_destination_ids,
		...restData
	} = useMemo(() => filterValues, [filterValues])
	// get all installment ids and crebit and debit account details
	const { data, isLoading } = useSWR(
		"outgoing_multi_payments_log" +
			queryToSearch(filterValues as Record<string, unknown>),
		() => {
			const due_after_local = due_after ? startOf(due_after, "day") : undefined
			const due_before_local = due_before ? endOf(due_before, "day") : undefined
			return instalmentsXHR(xhr).getBulkInstallmentPaymentInfo({
				...restData,
				include: "get_credit_debit_accounts",
				hide_on_hold: 1,
				is_due: 1,
				currencies: [currency],
				transportServiceProviders: transportServiceProviders?.id
					? [transportServiceProviders?.id]
					: [],
				is_debit: paymentTnxType === "debit" ? 1 : 0,
				paymentable_types: paymentable_types?.id ? [paymentable_types?.id] : [],
				hotels: [hotels?.id],
				trip_id: trip_id?.id,
				due_after_local: due_after_local ? dateToQuery(due_after_local) : null,
				due_after: due_after_local ? dateToUTCString(due_after_local) : null,
				due_before_local: due_before_local
					? dateToQuery(due_before_local)
					: null,
				due_before: due_before_local ? dateToUTCString(due_before_local) : null,
				only_direct_hotel_payments: hotels?.id ? 1 : undefined,
				trip_destination_ids: trip_destination_ids?.map((t) => t.id),
			})
		}
	)

	const { allInstallmentIds, allInstallmentSummaries } = useMemo(() => {
		return (data?.data || []).reduce(
			(acc, v) => {
				acc.allInstallmentIds.push(v.id)
				acc.allInstallmentSummaries.push({
					id: v.id,
					currency: v.currency,
					amount: v.amount,
				})
				return acc
			},
			{
				allInstallmentIds: [] as number[],
				allInstallmentSummaries: [] as Array<{
					id: number
					currency: string
					amount: number
				}>,
			}
		)
	}, [data])

	const { chunkedIds, loadMore, hasMore, page, reset } = useChunkedData(
		allInstallmentIds,
		chunkSize
	)

	const fetchMoreInstallments = useCallback(async () => {
		setRequestStatus((v) => ({ ...v, isLoading: true }))
		try {
			const item = await instalmentsXHR(xhr).getInstallmentsList({
				included_ids: chunkedIds,
				sort: filterValues.sort || "due_date",
			})
			setInstallmentList((v) =>
				page === 1 ? item.data : [...(v || []), ...item.data]
			)
			setRequestStatus(() => ({ errorMessage: "", isLoading: false }))
		} catch (e) {
			setRequestStatus({
				isLoading: false,
				errorMessage: (e as Error).message,
			})
		}
	}, [xhr, chunkedIds, page, filterValues])

	useEffect(() => {
		if (chunkedIds.length) {
			fetchMoreInstallments()
		}
	}, [chunkedIds, fetchMoreInstallments])

	function onRefresh() {
		setInstallmentList([])
		reset()
	}

	const onSubmit = useCallback(() => {
		onFilterChange({})
		onClose()
		onSuccess()
	}, [onFilterChange, onClose, onSuccess])

	return (
		<Dialog
			lg
			onClose={onClose}
			open={step === "finalStep"}
			title="Log payment"
		>
			<DialogBody>
				<Stack gap="4">
					<Stack
						borderWidth="1"
						borderColor="muted"
						rounded="lg"
						gap={"2"}
						paddingBottom={"4"}
					>
						<Box
							paddingLeft={"4"}
							bgColor={"inset"}
							roundedTop="lg"
							paddingY="4"
						>
							<Heading fontSize={"base"}>
								Please review the full payment details
							</Heading>
						</Box>
						<Stack padding={"4"} gap="4">
							<Box>
								<Inline fontWeight="semibold" gap={"4"}>
									<Stack gap={"2"}>
										<Text fontSize={"sm"} color="muted">
											TOTAL AMOUNT ({filterValues.currency})
										</Text>
										<Text fontSize={"md"} color="accent">
											{data?.meta?.total_amount_money?.formatted ?? 0}
										</Text>
									</Stack>
									<Stack gap={"2"}>
										<Text fontSize={"sm"} color="muted">
											TOTAL INSTALMENTS
										</Text>
										<Text fontSize={"md"} color="accent">
											{data?.data.length || 0}
										</Text>
									</Stack>
								</Inline>
								<Divider sm />
							</Box>
						</Stack>
						{requestStatus.errorMessage ? (
							<RefreshComponent
								onRefresh={onRefresh}
								message={requestStatus.errorMessage}
							/>
						) : null}
						<Box
							style={{
								maxHeight: 286,
								overflowY: "auto",
							}}
							position={"relative"}
							paddingX={"4"}
						>
							<BulkPaymentInstalmentTable
								showContact
								instalments={installentList}
							/>
							<Box marginTop="4" textAlign="center">
								{installentList.length ? (
									<Text color="muted" fontSize="sm">
										Showing {installentList.length} of {data?.data.length}
									</Text>
								) : null}
								{hasMore ? (
									requestStatus.isLoading ? (
										<SpinnerWithMessage message={"Loading date ..."} />
									) : (
										<Button
											disabled={requestStatus.isLoading}
											onClick={loadMore}
										>
											Load more
										</Button>
									)
								) : null}
							</Box>
						</Box>
					</Stack>
					{isLoading ? null : (
						<PaymentTransactionDetails
							onSubmit={onSubmit}
							onPrevious={() => {
								onPrevious()
								setInstallmentList([])
							}}
							isIncomingTxn={paymentTnxType !== "debit"}
							instalmentCurrency={filterValues.currency as string}
							totalInstalmentAmount={
								data?.meta?.total_amount_money
									? formatMoneyByDecimal({
											...data?.meta.total_amount_money,
											amount: Number(data?.meta.total_amount_money.amount),
										})
									: undefined
							}
							debitableAccounts={data?.meta?.debit_accounts}
							creditableAccounts={data?.meta?.credit_accounts}
							allInstallmentSummaries={allInstallmentSummaries}
						/>
					)}
				</Stack>
			</DialogBody>
		</Dialog>
	)
}

export function SelectPaymentType({
	supplierIds,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	supplierIds: number[] | number | undefined
}) {
	const xhr = useXHR()
	const hasArray = Array.isArray(supplierIds)
	return (
		<AsyncSelect
			multiple
			{...props}
			fetch={(q) =>
				instalmentsXHR(xhr)
					.getpaymentsType({
						q,
						supplier_ids: hasArray
							? (supplierIds as number[])
							: supplierIds
								? [supplierIds]
								: [],
					})
					.then((resp) => resp.data)
			}
			cacheKey={`suppliers-ids-${
				hasArray
					? (supplierIds as number[]).map((t) => String(t)).join("-")
					: (supplierIds ?? "")
			}`}
		/>
	)
}

export function PaymentTransactionDetails({
	onSubmit,
	instalmentCurrency,
	isIncomingTxn,
	debitableAccounts,
	creditableAccounts,
	totalInstalmentAmount,
	onPrevious,

	allInstallmentSummaries,
}: {
	debitableAccounts?: IAccount[]
	creditableAccounts?: IAccount[]
	instalmentCurrency: string
	isIncomingTxn: boolean
	onSubmit: () => void
	totalInstalmentAmount: string | undefined
	onPrevious: () => void
	allInstallmentSummaries:
		| Array<{
				id: number
				currency: string
				amount: number
		  }>
		| undefined
}) {
	const { user } = useAuthUser()
	const xhr = useXHR()

	const { init, hasPending, hasError, isSuccess } = useBatch()

	useEffect(() => {
		if (isSuccess) {
			onSubmit()
			showSnackbar("Payment logged successfully!")
		}
	}, [isSuccess, onSubmit])
	useEffect(() => {
		if (hasError) {
			showSnackbar("Payment logging process failed!")
		}
	}, [hasError])

	const functional_currency = user?.tenant?.functional_currency

	const defaultDebitAccount = useMemo(() => {
		const accounts = debitableAccounts || []
		return (
			accounts.find((a) => a?.asset_type === instalmentCurrency) || accounts[0]
		)
	}, [debitableAccounts, instalmentCurrency])

	const defaultCreditAccount = useMemo(() => {
		const accounts = creditableAccounts || []
		return (
			accounts.find((a) => a?.asset_type === instalmentCurrency) || accounts[0]
		)
	}, [creditableAccounts, instalmentCurrency])

	const txnCurrency =
		(isIncomingTxn
			? defaultCreditAccount?.asset_type
			: defaultDebitAccount?.asset_type) || functional_currency

	const initialValues = useMemo(() => {
		return {
			paid_amount: Number(totalInstalmentAmount) || 0,
			txn_currency: txnCurrency || "INR",
			currency_to_txn_currency_exchange_rate:
				instalmentCurrency === txnCurrency ? 1 : 0,
			txn_amount:
				instalmentCurrency === txnCurrency ? totalInstalmentAmount || 0 : 0,
			paid_at: undefined as Date | undefined,
			reference_id: "",
			debit_account: defaultDebitAccount
				? {
						...defaultDebitAccount,
						name: defaultDebitAccount.asset_type
							? `${defaultDebitAccount.name} (${defaultDebitAccount.asset_type})`
							: defaultDebitAccount.name,
					}
				: undefined,
			credit_account: defaultCreditAccount
				? {
						...defaultCreditAccount,
						name: defaultCreditAccount.asset_type
							? `${defaultCreditAccount.name} (${defaultCreditAccount.asset_type})`
							: defaultCreditAccount.name,
					}
				: undefined,
			partial_payment: false,
		}
	}, [
		totalInstalmentAmount,
		instalmentCurrency,
		txnCurrency,
		defaultDebitAccount,
		defaultCreditAccount,
	])

	return (
		<Stack gap="4">
			<Heading as="h4">Payment Transaction details</Heading>
			<Form<typeof initialValues>
				initialValues={initialValues}
				validate={validateFormValues(
					Validator.object()
						.required()
						.shape({
							paid_amount: EmptyNumberValidator()
								.positive("Amount should be positive")
								.max(
									initialValues.paid_amount,
									"Amount can not be greater than instalment"
								),
							txn_amount: EmptyNumberValidator().when(
								"txn_currency",
								([txn_currency], schema) => {
									if (!!txn_currency && instalmentCurrency !== txn_currency) {
										return schema
											.required(
												"Please provide the amount in mentioned currency."
											)
											.positive("Please provide a value.")
									}
									return schema.notRequired()
								}
							),
							paid_at: Validator.date().required(
								"Please provide a date when the payment was made"
							),
							debit_account: Validator.object().required(
								"Please select a debit account from which the payment is made"
							),
							credit_account: Validator.object().required(
								"Please select a credit account where the payment is made"
							),
							reference_id: Validator.string().required(
								"Please provide a reference id."
							),
							confirmation_amount: Validator.number().when(
								"paid_amount",
								(paid_amount, schema) => {
									return schema
										.equals(
											paid_amount,
											`Please type ${paid_amount} to confirm the payment log`
										)
										.notOneOf([0], "Please type a valid number")
										.required("Please type the count to confirm")
								}
							),
						})
				)}
				onSubmit={withServerErrors(
					async ({
						paid_amount,
						txn_currency,
						txn_amount,
						reference_id,
						debit_account,
						credit_account,
						paid_at,
						partial_payment,
					}) => {
						if (!debit_account || !credit_account) {
							throw Error(
								"Please fill the required data e.g. debit/credit accounts."
							)
						}

						const resp = await xhr.post(`/bulk-payment-instalments`, {
							paid_amount_currency: instalmentCurrency,
							paid_amount,
							txn_currency: txn_currency,
							txn_amount:
								instalmentCurrency === txn_currency ? paid_amount : txn_amount,
							paid_at: paid_at
								? dateToUTCString(paid_at)
								: dateToUTCString(new Date()),
							reference_id,
							debit_account_id: debit_account.id,
							credit_account_id: credit_account.id,

							instalments: allInstallmentSummaries,
							partial_payment: partial_payment ? 1 : 0,
						})
						init(resp.data.data)
						showSnackbar("Processing payments... Please wait.")
					}
				)}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit, form }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="6">
							<Grid gap="4">
								<Col>
									<GetFieldValue<IAccount> name="debit_account">
										{({ value: debit_account }) => (
											<SelectField
												label="Debit Account"
												select={SelectAccount}
												name="debit_account"
												defaultOptions={
													(debitableAccounts || []).map((a) => ({
														...a,
														name: a.asset_type
															? `${a.name} (${a.asset_type})`
															: a.name,
													})) || undefined
												}
												onChange={(value: IAccount) => {
													form.change("debit_account", value)
													if (!isIncomingTxn) {
														form.change(
															"txn_currency",
															value?.asset_type || functional_currency
														)
														if (
															(debit_account?.asset_type ||
																functional_currency) !== value?.asset_type
														) {
															form.change("txn_amount", 0)
															form.change(
																"currency_to_txn_currency_exchange_rate",
																0
															)
														}
													}
												}}
											/>
										)}
									</GetFieldValue>
								</Col>
								<Col>
									<GetFieldValue<IAccount> name="credit_account">
										{({ value: credit_account }) => (
											<SelectField
												label="Credit Account"
												select={SelectAccount}
												name="credit_account"
												onChange={(value: IAccount) => {
													form.change("credit_account", value)
													if (isIncomingTxn) {
														form.change(
															"txn_currency",
															value?.asset_type || functional_currency
														)
														if (
															(credit_account?.asset_type ||
																functional_currency) !== value?.asset_type
														) {
															form.change("txn_amount", 0)
															form.change(
																"currency_to_txn_currency_exchange_rate",
																0
															)
														}
													}
												}}
											/>
										)}
									</GetFieldValue>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="4">
								<Col xs={12} sm="auto">
									<Inline gap="4" collapseBelow="md">
										<Box
											display={"flex"}
											gap={"2"}
											flex="1"
											style={{ maxWidth: "150px" }}
											flexDirection={"col"}
										>
											<GetFieldValue<boolean> name="partial_payment">
												{({ value: parcialPayment }) => (
													<TextInputField
														label={`Paid Amount (${instalmentCurrency})`}
														name="paid_amount"
														type="number"
														required
														readOnly={!parcialPayment}
														min={0}
														max={initialValues.paid_amount}
														onChange={(e) => {
															const value = e.currentTarget.value
															form.change("paid_amount", value as never)
															if (!value) {
																form.change("txn_amount", 0)
															} else {
																const txnCurrency =
																	form.getFieldState("txn_currency")?.value
																const exchangeRate = form.getFieldState(
																	"currency_to_txn_currency_exchange_rate"
																)?.value
																form.change(
																	"txn_amount",
																	formatMoneyByDecimal(
																		convertMoneyToCurrency(
																			moneyParseByDecimal(
																				Number(value) || 0,
																				instalmentCurrency
																			),
																			txnCurrency || "INR",
																			Number(exchangeRate || 1) || 0
																		)
																	) as never
																)
															}
														}}
													/>
												)}
											</GetFieldValue>
											<CheckboxInputField
												label={"Partial Payment"}
												name={`partial_payment`}
												fontSize={"xs"}
												onChange={(e) => {
													form.change(
														"partial_payment",
														e.currentTarget.checked
													)
													if (!e.currentTarget.checked) {
														const txnCurrency =
															form.getFieldState("txn_currency")?.value
														const exchangeRate = form.getFieldState(
															"currency_to_txn_currency_exchange_rate"
														)?.value
														const paidAmount = initialValues.paid_amount || 0
														form.batch(() => {
															form.change("paid_amount", paidAmount)
															form.change(
																"txn_amount",
																formatMoneyByDecimal(
																	convertMoneyToCurrency(
																		moneyParseByDecimal(
																			Number(paidAmount) || 0,
																			instalmentCurrency
																		),
																		txnCurrency || "INR",
																		Number(exchangeRate || 0) || 0
																	)
																) as never
															)
														})
													}
												}}
											/>
										</Box>
										<GetFieldValue<string> name={`txn_currency`}>
											{({ value: txn_currency }) =>
												txn_currency && instalmentCurrency !== txn_currency ? (
													<GetFieldValue<number> name="paid_amount">
														{({ value: paidAmount }) => (
															<Inline>
																<TextInputField
																	label={`Exchange (${instalmentCurrency}/${txn_currency})`}
																	name="currency_to_txn_currency_exchange_rate"
																	type="number"
																	placeholder="Exchange Rate"
																	min={0}
																	required
																	style={{ maxWidth: "150px" }}
																	help={({ value }) =>
																		value !== "" ? (
																			<Text>
																				{currencyPairToHuman(
																					makeCurrencyPair(
																						instalmentCurrency,
																						txn_currency || "INR",
																						Number(value) || 0
																					)
																				)}
																			</Text>
																		) : null
																	}
																	onChange={(e) => {
																		const value = e.currentTarget.value
																		form.change(
																			"currency_to_txn_currency_exchange_rate",
																			value as never
																		)
																		if (!value) {
																			form.change("txn_amount", 0)
																		} else {
																			form.change(
																				"txn_amount",
																				formatMoneyByDecimal(
																					convertMoneyToCurrency(
																						moneyParseByDecimal(
																							paidAmount || 0,
																							instalmentCurrency
																						),
																						txn_currency || "INR",
																						Number(value || 0) || 0
																					)
																				) as never
																			)
																		}
																	}}
																/>
																<Box paddingX="2" paddingTop="6">
																	<Icons.SwitchHorizontal />
																</Box>
																<TextInputField
																	label={`Amount (${txn_currency})`}
																	name="txn_amount"
																	type="number"
																	min={0}
																	required
																	placeholder="Amount"
																	style={{ maxWidth: "150px" }}
																	onChange={(
																		e: React.FormEvent<HTMLInputElement>
																	) => {
																		const value = e.currentTarget.value
																		const amount = Number(value)
																		const paid = Number(paidAmount)

																		const rate =
																			!value || amount === 0 ? 0 : amount / paid

																		form.batch(() => {
																			form.change("txn_amount", value as never)
																			form.change(
																				"currency_to_txn_currency_exchange_rate",
																				Number(rate.toFixed(6))
																			)
																		})
																	}}
																/>
															</Inline>
														)}
													</GetFieldValue>
												) : null
											}
										</GetFieldValue>
									</Inline>
								</Col>
								<Col>
									<DatePickerField
										label="Paid on"
										name="paid_at"
										dateFormat
										timeFormat
										required
										rightAlign
										max={new Date()}
									/>
								</Col>
							</Grid>
							<Stack gap="2">
								<TextInputField
									name="reference_id"
									label="Reference Id"
									type="text"
									placeholder="Reference Id of the payment"
								/>
								<GetFieldValue<number> name="paid_amount">
									{({ value: paidAmount }) => (
										<TextInputField
											label={`Please Type ${paidAmount} to confirm`}
											name="confirmation_amount"
											type="number"
											aria-label="Type in the number of the Services to confirm that you want to disable these Services"
											required
										/>
									)}
								</GetFieldValue>
							</Stack>
							<Divider sm />
							<SubmissionError />
							<Inline gap="4">
								<Button
									disabled={submitting || (hasPending && !hasError)}
									type="submit"
								>
									{submitting || (hasPending && !hasError)
										? "Saving..."
										: hasError
											? "Retry"
											: "Save"}
								</Button>

								<Button
									onClick={onPrevious}
									level="tertiary"
									disabled={submitting}
								>
									Back
								</Button>
							</Inline>
						</Stack>
					</form>
				)}
			</Form>
		</Stack>
	)
}

type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

function useBatch() {
	const [batch, setBatch] = useState<TBatch | null>(null)

	const xhr = useXHR()

	const { data: currentBatch } = useSWR(
		batch ? `/batch/${batch.id}` : null,
		() =>
			xhr
				.get<{ data: TBatch }>(`batches/${batch?.id}`)
				.then((resp) => resp.data.data)
				.then((batch) => {
					return batch
				}),
		{
			refreshInterval: (data) => (data?.finishedAt ? 0 : 1000),
		}
	)

	const hasPending = !!batch && currentBatch && !!currentBatch?.pendingJobs
	const hasError = !!currentBatch?.finishedAt && hasPending
	const isSuccess = !!currentBatch?.finishedAt && !hasPending && currentBatch

	return {
		init: (batch: TBatch) => {
			setBatch(batch)
		},
		hasPending,
		hasError,
		isSuccess,
	}
}

interface BulkInstalmentTableProps {
	instalments: Array<IInstalment>
	isSelectAll?: boolean
	checkedIds?: number[]
	onCheckedIdsChange?: React.Dispatch<React.SetStateAction<number[]>>
	showCheckbox?: boolean
	showContact?: boolean
}

export function BulkPaymentInstalmentTable({
	instalments,
	showCheckbox = false,
	showContact = false,
	isSelectAll,
	checkedIds,
	onCheckedIdsChange,
}: BulkInstalmentTableProps) {
	const safeIsSelectAll = useMemo(() => isSelectAll ?? false, [isSelectAll])
	const safeCheckedIds = useMemo(() => checkedIds ?? [], [checkedIds])

	const headers = useMemo(() => {
		const cols = []
		if (showCheckbox) cols.push("")
		cols.push("Amount")
		if (showContact) cols.push("Contact")
		cols.push("Due Date")
		return cols
	}, [showCheckbox, showContact])

	const rows = useMemo(
		() =>
			instalments.map((instalment) => {
				const { currency, amount, due_at, due_at_local, id } = instalment

				const isChecked = showCheckbox
					? safeIsSelectAll
						? !safeCheckedIds.includes(id)
						: safeCheckedIds.includes(id)
					: false

				const toggleCheckbox = (checked: boolean) => {
					if (!showCheckbox || !onCheckedIdsChange) return

					const shouldRemove =
						(checked && safeIsSelectAll) || (!checked && !safeIsSelectAll)

					onCheckedIdsChange((ids = []) =>
						shouldRemove
							? ids.filter((singleId) => singleId !== id)
							: [...ids, id]
					)
				}

				const row: React.ReactNode[] = []

				if (showCheckbox) {
					row.push(
						<CheckboxInput
							id={id.toString()}
							checked={isChecked}
							onChange={(e) => toggleCheckbox(e.currentTarget.checked)}
						/>
					)
				}

				row.push(
					<Box>
						<InstalmentItemInDialog
							instalmentId={id}
							showSiblings
							readOnly
							disableLogging
							hideRevert
						>
							{({ show }) => (
								<Button inline onClick={show}>
									<Text as="span" fontSize="md" color="accent">
										<Money amount={amount} currency={currency} showCurrency />
									</Text>
								</Button>
							)}
						</InstalmentItemInDialog>
					</Box>
				)

				if (showContact) {
					row.push(<InstalmentContact instalment={instalment} />)
				}

				row.push(
					<Box>
						<RelativeTime
							timestamp={due_at}
							localTimestamp={due_at_local}
							minUnit="day"
						/>
					</Box>
				)

				return row
			}),
		[
			instalments,
			showCheckbox,
			showContact,
			safeCheckedIds,
			safeIsSelectAll,
			onCheckedIdsChange,
		]
	)

	return (
		<Table
			stickyHeader
			striped
			bordered
			hover
			responsive
			headers={headers}
			rows={rows}
		/>
	)
}

export function RefreshComponent({
	onRefresh,
	message,
}: {
	onRefresh: () => void
	message: string
}) {
	return (
		<Inline
			alignItems="center"
			gap={"3"}
			padding={"3"}
			borderColor={"danger"}
			borderRadius="md"
		>
			<Box as="span" flex="1" fontWeight="semibold">
				{message}
			</Box>
			<Button color="white" level="primary" type="reset" onClick={onRefresh}>
				Refresh
			</Button>
		</Inline>
	)
}

export function SpinnerWithMessage({ message }: { message: string }) {
	return (
		<Inline justifyContent="center" gap="2" alignItems="center">
			<Spinner /> {message}
		</Inline>
	)
}

function useChunkedData(ids: Array<number>, chunkSize = 10) {
	const [page, setPage] = useState(1)

	const chunkedIds = useMemo(() => {
		if (!ids.length) return []
		const start = (page - 1) * chunkSize
		const end = start + chunkSize
		return ids.slice(start, end)
	}, [ids, page, chunkSize])

	const loadMore = useCallback(() => {
		setPage((p) => p + 1)
	}, [])

	const reset = useCallback(() => {
		setPage(1)
	}, [])

	const hasMore = page * chunkSize < ids.length

	return {
		chunkedIds,
		loadMore,
		reset,
		hasMore,
		page,
	}
}
